import {
  DATA_SOURCES,
  isPastStaleAfter,
  type DataSourceId,
} from '@shared/data-sources';
import type { DataSourceFreshness } from '@shared/types/data-freshness';
import type { StatusTone } from '@/components/status-tone';
import { formatRelative } from '@/lib/formatters';

/**
 * How long ago a sync was, from `formatRelative`. A sync stamped a little after `now` (the
 * server's clock ahead of the browser's) reads "just now", never "in 1m".
 */
export function syncedAgo(atMs: number, now: number): string {
  return formatRelative(Math.min(atMs, now), now);
}

export type SourceState = 'fresh' | 'stale' | 'never' | 'sync_failed' | 'reconnect' | 'not_connected';

export interface SourceLine {
  source: DataSourceId;
  label: string;
  state: SourceState;
  tone: StatusTone;
  /** When it last synced (epoch ms), or null. */
  syncedAt: number | null;
}

export interface FreshnessSummary {
  /** The chip's colour: the worst source wins. */
  tone: StatusTone;
  /** The chip's words, e.g. "Updated 3h ago", "Sync failed", "Reconnect Xero". */
  label: string;
  /** One line per source, for the tooltip. */
  lines: SourceLine[];
}

const STATE_TONE: Record<SourceState, StatusTone> = {
  fresh: 'success',
  stale: 'warning',
  never: 'warning',
  sync_failed: 'danger',
  reconnect: 'danger',
  not_connected: 'neutral',
};

const TONE_RANK: Record<StatusTone, number> = { danger: 4, warning: 3, info: 2, success: 1, neutral: 0 };

function sourceState(entry: DataSourceFreshness | undefined, now: number): SourceState {
  if (!entry || !entry.connected) return 'not_connected';
  if (entry.issue) return entry.issue;
  if (!entry.last_synced_at) return 'never';
  return isPastStaleAfter(entry.last_synced_at, DATA_SOURCES[entry.source].staleAfterHours, now)
    ? 'stale'
    : 'fresh';
}

function parsedAt(entry: DataSourceFreshness | undefined): number | null {
  if (!entry?.last_synced_at) return null;
  const at = Date.parse(entry.last_synced_at);
  return Number.isFinite(at) ? at : null;
}

/**
 * Sums up a page's sources for the DataFreshness chip. The age shown is the oldest connected
 * source's, so "Updated 3h ago" is true of everything on the page.
 */
export function summariseFreshness(
  sources: readonly DataSourceId[],
  entries: ReadonlyMap<DataSourceId, DataSourceFreshness>,
  now: number
): FreshnessSummary {
  const lines: SourceLine[] = sources.map((source) => {
    const entry = entries.get(source);
    const state = sourceState(entry, now);
    return {
      source,
      label: DATA_SOURCES[source].label,
      state,
      tone: STATE_TONE[state],
      syncedAt: parsedAt(entry),
    };
  });

  const worst = lines.reduce<SourceLine | null>(
    (acc, line) => (!acc || TONE_RANK[line.tone] > TONE_RANK[acc.tone] ? line : acc),
    null
  );
  const connected = lines.filter((line) => line.state !== 'not_connected');
  const oldest = connected
    .map((line) => line.syncedAt)
    .filter((at): at is number => at != null)
    .reduce<number | null>((acc, at) => (acc == null || at < acc ? at : acc), null);

  let label: string;
  if (!worst || connected.length === 0) {
    label = 'Not connected';
  } else if (worst.state === 'reconnect') {
    label = `Reconnect ${worst.label}`;
  } else if (worst.state === 'sync_failed') {
    label = 'Sync failed';
  } else if (oldest == null || connected.some((line) => line.state === 'never')) {
    label = 'Not synced yet';
  } else {
    label = `Updated ${syncedAgo(oldest, now)}`;
  }
  return { tone: worst?.tone ?? 'neutral', label, lines };
}
